import { DataMapper, HttpTransport } from '@estee/elc-http-transport';
import { ELCLogger, TriggerType } from '@estee/elc-logging';
import { format } from 'date-fns';
import {
    signInErrorTransKey,
    dateFnsDateToken,
    businessUnitConfigNode,
    SearchParam,
    CSRF_TOKEN_COOKIE_NAME
} from '../constants/Constants';
import { LoginType } from '../constants/LoginTypeEnum';
import { IFacebookLogin, ILogin } from '../internal/domain/entities/Login';
import { IUserFormValues } from '../internal/domain/entities/UserFormStore';
import { SignInError } from './DataMappings';

// User status key returned from API indicates matched user and registration status with given identifiers
export enum SignUpUserStatusKey {
    EXISTING_PARTIAL = 'existing.partial',
    EXISTING_PARTIAL_OFFLINE = 'existing.partial.offline',
    EXISTING_COMPLETE = 'existing.complete',
    EXISTING_COMPLETE_OFFLINE = 'existing.complete.offline',
    NEW_PARTIAL_OFFLINE = 'new.partial.offline'
}

export type FeatureProcessResultType = {
    // 1 means success, other codes indicate unsuccessful results depends on feature processed
    resultCode: number;
};

export interface SignUpUserResultEntry {
    userId: number;
    userStatusKey: SignUpUserStatusKey;
    userUpdated: 1 | 0;
}

export interface IUserApiResponse {
    features: {
        [key: string]: FeatureProcessResultType;
    } | null;
    users: SignUpUserResultEntry[];
}

export type SignUpResponse = {
    userId: number;
    userStatusKey: SignUpUserStatusKey;
    posFrontiersErrorKey?: string;
};

export interface IUserSignInApiResponse {
    errors?: [{ key: string }];
    success?: number;
}

export interface IRampPgApiConfig {
    regionId: string;
    brandId: string;
    token: string;
    baseUrl: string;
    businessUnitId: string;
}

export interface IGlobalHeaders {
    Authorization: string;
    AuthorizationCredentials: string;
}

enum RequestHeader {
    BUID = 'business-unit-id',
    BU_CONFIG_NODE = 'business-unit-config-node'
}

export class RampPgApiSdk {
    httpTransport: HttpTransport;
    logger: ELCLogger;
    userPath = '/rest/api/v1/user';
    searchParams = new Map<string, string>();
    dataMapper: DataMapper;
    businessUnitId: string;

    constructor(logger: ELCLogger, httpTransport: HttpTransport, dataMapper: DataMapper) {
        this.httpTransport = httpTransport;
        this.logger = logger;
        this.dataMapper = dataMapper;
    }

    private getMockHeaders = (pcEmailAddress?: string) => {
        const header: Record<string, string> = {
            // Private Postman mock server key
            'x-api-key': 'PMAK-611383b9fc86d90040032d60-691ac56afdc55eaea7b339c81acc10b2b5'
        };
        if (pcEmailAddress) {
            return {
                ...header,
                // Headers used to differentiate scenarios
                'x-mock-match-request-headers': 'pc-email',
                'pc-email': pcEmailAddress
            };
        }

        return header;
    };

    private getUserApiHeaders(pcEmailAddress?: string) {
        return {
            [RequestHeader.BUID]: this.businessUnitId,
            [RequestHeader.BU_CONFIG_NODE]: businessUnitConfigNode,
            ...(USE_MOCK_SERVER && this.getMockHeaders(pcEmailAddress))
        };
    }

    private getGlobalHeaders = (token: string): IGlobalHeaders => {
        const AuthorizationCredentials = `Basic ${token}`;
        const Authorization = AuthorizationCredentials;

        // add the header authorization twice for ios support
        return {
            Authorization,
            AuthorizationCredentials
        };
    };

    private getCookie = (name: string) => {
        const cookies = `; ${document.cookie}`;
        const parts = cookies.split(`; ${name}=`);
        if (parts.length === 2) {
            return decodeURIComponent(parts.pop()?.split(';').shift() || '');
        } else {
            return null;
        }
    };

    public setup(config: IRampPgApiConfig): void {
        const { baseUrl, token, regionId, brandId, businessUnitId } = config;
        this.searchParams.set(SearchParam.REGION, regionId);
        this.searchParams.set(SearchParam.BRAND, brandId);
        this.businessUnitId = businessUnitId;
        this.httpTransport.setBaseUrl(baseUrl);
        this.httpTransport.addGlobalHeaders(this.getGlobalHeaders(token));
    }

    private logApiError = (requestUrl: string, operation = '') =>
        this.logger.error({
            message: `[RAMP]${operation}: ${requestUrl}`,
            triggerType: TriggerType.api
        });

    private getRequestUrl = (
        resourcePaths: (string | number)[] = [],
        extraSearchParams: Map<string, string> = new Map<string, string>()
    ) => {
        const urlSearchParams = new URLSearchParams([
            ...this.searchParams.entries(),
            ...extraSearchParams.entries()
        ]);

        return `${resourcePaths.filter((path) => !!path).join('/')}?${urlSearchParams}`;
    };

    public addUser = async (
        data: IUserFormValues
    ): Promise<SignUpResponse | Record<string, never>> => {
        const requestUrl = this.getRequestUrl([this.userPath]);
        try {
            const payload = {
                url: requestUrl,
                body: this.dataMapper.mapRequest('toAddUser', data),
                headers: this.getUserApiHeaders(data.pcEmailAddress)
            };

            const response = await this.httpTransport.post(payload);

            return this.dataMapper.mapResponse('fromAddUser', response.data as IUserApiResponse);
        } catch (e) {
            if (e.status === 409 && e.errorResponse?.data) {
                return this.dataMapper.mapResponse(
                    'fromAddUser',
                    e.errorResponse?.data as IUserApiResponse
                );
            } else {
                this.logApiError(requestUrl);

                return {};
            }
        }
    };

    public updateUser = async (
        userId: number,
        data: IUserFormValues
    ): Promise<SignUpResponse | Record<string, never>> => {
        const requestUrl = this.getRequestUrl([this.userPath, userId]);
        try {
            const payload = {
                url: requestUrl,
                body: this.dataMapper.mapRequest('toUpdateUser', data),
                headers: this.getUserApiHeaders()
            };

            const response = await this.httpTransport.put(payload);

            return this.dataMapper.mapResponse('fromUpdateUser', response.data as IUserApiResponse);
        } catch (_) {
            this.logApiError(requestUrl);

            return {};
        }
    };

    public patchPassword = async (userId: number, password: string) => {
        const requestUrl = this.getRequestUrl([this.userPath, userId]);
        try {
            const payload = {
                url: requestUrl,
                body: { password },
                headers: this.getUserApiHeaders()
            };

            const response = await this.httpTransport.patch(payload);

            const { userStatusKey } = this.dataMapper.mapResponse(
                'patchPassword',
                response.data as IUserApiResponse
            ) as SignUpResponse | Record<string, never>;

            return userStatusKey === SignUpUserStatusKey.EXISTING_COMPLETE_OFFLINE;
        } catch {
            this.logApiError(requestUrl);

            return false;
        }
    };

    public signIn = async (
        formData: ILogin | IFacebookLogin,
        loginType?: LoginType
    ): Promise<SignInError | ''> => {
        const requestUrl = this.getRequestUrl([this.userPath, 'session', loginType ?? '']);
        const data = this.dataMapper.mapRequest('toSignIn', { formData, loginType });
        const csrfToken = this.getCookie(CSRF_TOKEN_COOKIE_NAME);

        try {
            const payload = {
                url: requestUrl,
                body: {
                    ...data,
                    csrfToken
                },
                options: {
                    credentials: 'include'
                }
            };
            const { data: apiResponse } = await this.httpTransport.post(payload);

            return (apiResponse as IUserSignInApiResponse)?.success ? '' : signInErrorTransKey;
        } catch (e) {
            this.logApiError(requestUrl);

            return this.dataMapper.mapResponse(
                'fromSignInError',
                e.errorResponse?.data
            ) as SignInError;
        }
    };

    public loadStaticJsonContent = async (url: string) => {
        try {
            const response = await fetch(`${url}?t=${format(new Date(), dateFnsDateToken)}`);

            return response.ok ? response.json() : [];
        } catch {
            return [];
        }
    };
}
